import {
  FieldFilter,
  isComparisonOperator,
  numericFilter,
  parseNumericBound,
} from "@/v2/components/filter-menu/FieldFilter.model";
import {
  Filter,
  isAndFilter,
  isFieldFilter,
} from "@/v2/entities/explore/Explore.filter";
import { ExploreFiltersValue } from "./ExploreFilterMenu";

function isString(value: unknown): value is string {
  return typeof value === "string";
}

function isNumber(value: unknown): value is number {
  return typeof value === "number";
}

function isBoolean(value: unknown): value is boolean {
  return typeof value === "boolean";
}

function isStringArray(value: unknown): value is string[] {
  return (
    Array.isArray(value) && value.every((item) => typeof item === "string")
  );
}

const DELIMITER = "|";

export function addFiltersToSearchParams(
  searchParams: URLSearchParams,
  value: Partial<Record<string, FieldFilter>>
) {
  for (const [field, filter] of Object.entries(value)) {
    if (filter != null) {
      switch (filter.operator) {
        case "eq":
        case "ne":
        case "gte":
        case "lte":
          searchParams.set(
            field,
            [filter.operator, filter.value].join(DELIMITER)
          );
          break;
        case "in":
        case "nin":
          searchParams.set(
            field,
            [filter.operator, ...filter.value].join(DELIMITER)
          );
          break;
        case "isnull":
        case "isnotnull":
          searchParams.set(field, filter.operator);
          break;
        case "like":
          searchParams.set(field, filter.value);
          break;
        default:
          break;
      }
    }
  }
}

export function extractFiltersFromSearchParams(
  searchParams: URLSearchParams
): Partial<Record<string, FieldFilter>> {
  const filters: ExploreFiltersValue = {};

  for (const [field, filter] of searchParams.entries()) {
    const [operator, ...values] = filter.split(DELIMITER);

    switch (operator) {
      case "eq":
      case "ne":
        filters[field] = { operator, value: values[0] };
        break;
      case "in":
      case "nin":
        filters[field] = { operator, value: values };
        break;
      case "isnull":
      case "isnotnull":
        filters[field] = { operator, value: "true" };
        break;
      case "like":
        filters[field] = { operator, value: values[0] };
        break;
      case "gte":
      case "lte": {
        const amount = parseNumericBound(values[0]);

        if (amount != null) {
          filters[field] = numericFilter(operator, amount);
        }
        break;
      }
      default:
        break;
    }
  }

  return filters;
}

export function parseFiltersToFilterRequest(
  filters: ExploreFiltersValue
): Filter {
  const filter: Filter = {
    and: [],
  };

  for (const [key, fieldFilter] of Object.entries(filters)) {
    // explore has no between operator, ranges only exist in escalation rule conditions
    if (fieldFilter != null && fieldFilter.operator !== "between") {
      filter.and.push({
        [key]: {
          [fieldFilter.operator]: fieldFilter.value,
        },
      });
    }
  }

  return filter;
}

function buildFieldFilter(
  filter: Filter
): { key: string; value: FieldFilter } | undefined {
  if (!isFieldFilter(filter)) {
    return;
  }

  const fieldName = Object.keys(filter)[0];
  const fieldValue = filter[fieldName];

  const operator = Object.keys(fieldValue)[0];

  if (!isComparisonOperator(operator)) {
    return;
  }

  const filterValue = fieldValue[operator];

  if (
    operator === "eq" &&
    (isString(filterValue) || isNumber(filterValue) || isBoolean(filterValue))
  ) {
    return {
      key: fieldName,
      value: {
        operator: "eq",
        value: filterValue,
      },
    };
  } else if (
    operator === "ne" &&
    (isString(filterValue) || isNumber(filterValue) || isBoolean(filterValue))
  ) {
    return {
      key: fieldName,
      value: {
        operator: "ne",
        value: filterValue,
      },
    };
  } else if (operator === "in" && isStringArray(filterValue)) {
    return {
      key: fieldName,
      value: {
        operator: "in",
        value: filterValue,
      },
    };
  } else if (operator === "nin" && isStringArray(filterValue)) {
    return {
      key: fieldName,
      value: {
        operator: "nin",
        value: filterValue,
      },
    };
  } else if (operator === "gte" || operator === "lte") {
    const amount = parseNumericBound(filterValue);

    if (amount != null) {
      return {
        key: fieldName,
        value: numericFilter(operator, amount),
      };
    }
  } else if (operator === "isnull") {
    return {
      key: fieldName,
      value: {
        operator: "isnull",
        value: "",
      },
    };
  } else if (operator === "isnotnull") {
    return {
      key: fieldName,
      value: {
        operator: "isnotnull",
        value: "",
      },
    };
  }

  return;
}

export function buildFiltersFromFilter(filter: Filter): ExploreFiltersValue {
  const filters: ExploreFiltersValue = {};

  if (isAndFilter(filter)) {
    for (const filterPart of filter.and) {
      const result = buildFieldFilter(filterPart);

      if (result != null) {
        filters[result.key] = result.value;
      }
    }
  }

  return filters;
}

export function parseFiltersFromFilterString(
  filterString: string
): ExploreFiltersValue {
  try {
    const filter = JSON.parse(filterString);
    const result: ExploreFiltersValue = {};

    if (filter.and) {
      filter.and.forEach((condition: Filter) => {
        Object.entries(condition).forEach(([key, value]: [string, any]) => {
          if (typeof value === "object" && value !== null) {
            if (value.eq !== undefined) {
              result[key] = { operator: "eq", value: value.eq };
            } else if (value.ne !== undefined) {
              result[key] = { operator: "ne", value: value.ne };
            } else if (value.in !== undefined) {
              result[key] = { operator: "in", value: value.in };
            } else if (value.nin !== undefined) {
              result[key] = { operator: "nin", value: value.nin };
            } else if (value.gte !== undefined || value.lte !== undefined) {
              const operator = value.gte !== undefined ? "gte" : "lte";
              const amount = parseNumericBound(value.gte ?? value.lte);

              if (amount != null) {
                result[key] = numericFilter(operator, amount);
              }
            } else if (value.like !== undefined) {
              result[key] = { operator: "like", value: value.like };
            } else if (value.isnull !== undefined) {
              result[key] = { operator: "isnull", value: value.isnull };
            } else if (value.isnotnull !== undefined) {
              result[key] = { operator: "isnotnull", value: value.isnotnull };
            }
          }
        });
      });
    }

    return result;
  } catch (error) {
    console.error("Error parsing filter string:", error);
    return {};
  }
}
